import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { UserAchievement } from '@/types';
import { IconByName } from '@/lib/category-icons';

interface AchievementToastProps {
  achievements: UserAchievement[];
  onDismiss: () => void;
  autoDismissMs?: number;
}

export function AchievementToast({
  achievements,
  onDismiss,
  autoDismissMs = 5000,
}: AchievementToastProps) {
  // Auto-dismiss after timeout
  useEffect(() => {
    if (achievements.length === 0) return;

    const timer = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(timer);
  }, [achievements, onDismiss, autoDismissMs]);

  if (achievements.length === 0) return null;

  // Show only the first achievement (queue them)
  const achievement = achievements[0];
  if (!achievement) return null;

  const a = achievement.achievement;
  if (!a) return null;

  const tierColors = {
    bronze: 'from-amber-700 to-amber-900',
    silver: 'from-slate-400 to-slate-600',
    gold: 'from-yellow-400 to-amber-500',
    platinum: 'from-slate-300 to-slate-500',
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -20, scale: 0.9 }}
        className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50"
      >
        <div
          className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${tierColors[a.tier]} p-1 shadow-2xl`}
        >
          {/* Sparkle animation background */}
          <motion.div
            className="absolute inset-0 opacity-50"
            animate={{
              background: [
                'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.3) 0%, transparent 50%)',
                'radial-gradient(circle at 80% 80%, rgba(255,255,255,0.3) 0%, transparent 50%)',
                'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%)',
                'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.3) 0%, transparent 50%)',
              ],
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          <div className="relative bg-charcoal-900/95 rounded-xl p-4 flex items-center gap-4 min-w-[300px]">
            {/* Icon */}
            <motion.div
              initial={{ rotate: -30, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="w-16 h-16 rounded-xl bg-charcoal-800 flex items-center justify-center text-4xl"
            >
              <IconByName name={a.icon} className="w-10 h-10" />
            </motion.div>

            {/* Content */}
            <div className="flex-1">
              <motion.p
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-xs text-amber-400 font-medium uppercase tracking-wider mb-1"
              >
                Achievement Unlocked!
              </motion.p>
              <motion.h3
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg font-bold text-white"
              >
                {a.name}
              </motion.h3>
              <motion.p
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="text-sm text-slate-400"
              >
                {a.description}
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-2 flex items-center gap-2"
              >
                <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                  +{a.xpReward} XP
                </span>
                <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                  a.tier === 'gold' ? 'bg-yellow-500/20 text-yellow-400' :
                  a.tier === 'platinum' ? 'bg-purple-500/20 text-purple-400' :
                  a.tier === 'silver' ? 'bg-slate-500/20 text-slate-300' :
                  'bg-amber-500/20 text-amber-500'
                }`}>
                  {a.tier}
                </span>
              </motion.div>
            </div>

            {/* Close button */}
            <button
              onClick={onDismiss}
              className="absolute top-2 right-2 text-slate-500 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
